import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowRight } from 'lucide-preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { getProductsQueryOptions, postUserProductMutationFn } from '@/queries/products';
import { Product, ProductInterval } from '@/queries/products/types';
import { useSelector } from '@/redux/helper';
import { trackMP } from '@/shared/mp';

import { Button } from '../Buttons';
import ProductEligibilityResult from './ProductEligibilityResult';
import ProductIntervalSelector from './ProductIntervalSelector';
import ProductPrice from './ProductPrice';

interface SponsorshipSubscriptionProps {
  habitatId: string;
  entryPoint?: string; // for analytics only
}

const LEARN_MORE_PLACEHOLDER_IMAGE = 'https://zoolife.tv/assets/products/l2-learn-more.webp';

const getProductTitle = (productLookupKey: string | undefined) => {
  switch (productLookupKey) {
    case 'monthly-supporter-tier-1':
    case 'annual-supporter-tier-1':
      return 'HELPER';
    case 'monthly-supporter-tier-2':
    case 'annual-supporter-tier-2':
      return 'FRIEND';
    case 'monthly-supporter-tier-3':
    case 'annual-supporter-tier-3':
      return 'GUARDIAN';
    default:
      return null;
  }
};

const getProductColor = (productLookupKey: string | undefined) => {
  switch (productLookupKey) {
    case 'monthly-supporter-tier-1':
    case 'annual-supporter-tier-1':
      return 'aria-selected:bg-[#FBFFCA] aria-selected:border-[#CDD565]';
    case 'monthly-supporter-tier-2':
    case 'annual-supporter-tier-2':
      return 'aria-selected:bg-[#FFF2F8] aria-selected:border-[#FFDCEE]';
    case 'monthly-supporter-tier-3':
    case 'annual-supporter-tier-3':
      return 'aria-selected:bg-[#FFE1CC] aria-selected:border-[#FFB682]';
    default:
      return null;
  }
};

const SponsorshipSubscription = ({ habitatId, entryPoint }: SponsorshipSubscriptionProps) => {
  const habitat = useSelector((state) => state.allHabitats.find((h) => h._id === habitatId));
  const [productInterval, setProductInterval] = useState<ProductInterval>('month');
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const selectorRef = useRef<HTMLDivElement>(null);
  const learnMoreRef = useRef<HTMLDivElement>(null);

  const { data: productsByInterval } = useQuery({
    ...getProductsQueryOptions({
      habitatId,
      lookupKey:
        'monthly-supporter-tier-1,monthly-supporter-tier-2,monthly-supporter-tier-3,annual-supporter-tier-1,annual-supporter-tier-2,annual-supporter-tier-3',
    }),
    select: (data) => {
      const map = data.list.reduce((acc, product) => {
        if (!product.productLookupKey) return acc;
        acc.set(product.productLookupKey, product);
        return acc;
      }, new Map<string, Product>());
      return {
        month: [
          map.get('monthly-supporter-tier-1'),
          map.get('monthly-supporter-tier-2'),
          map.get('monthly-supporter-tier-3'),
        ].filter((product) => product != null),
        year: [
          map.get('annual-supporter-tier-1'),
          map.get('annual-supporter-tier-2'),
          map.get('annual-supporter-tier-3'),
        ].filter((product) => product != null),
      } as Partial<Record<ProductInterval, Product[]>>;
    },
  });

  const products = productsByInterval?.[productInterval];

  const { mutate: checkout, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  useEffect(() => {
    const container = containerRef.current;
    const children = showLearnMore ? learnMoreRef.current : selectorRef.current;
    if (!container || !children) return undefined;
    container.style.height = `${children.getBoundingClientRect().height}px`;
    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.borderBoxSize[0]?.blockSize || children.getBoundingClientRect().height;
      if (height == null) return;
      container.style.height = `${height}px`;
    });
    resizeObserver.observe(children);
    return () => {
      resizeObserver.unobserve(children);
      resizeObserver.disconnect();
    };
  }, [showLearnMore]);

  if (!habitat) return null;

  return (
    <div className="w-full max-w-[336px] overflow-hidden rounded-2xl bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]">
      <div ref={containerRef} className="relative w-full transition-[height]">
        {/* selector */}
        <div ref={selectorRef} className="flex w-full flex-col items-center gap-[13px] pb-6">
          <div className="relative h-[104px] w-full rounded-t-2xl bg-gray-400">
            {habitat.backgroundImage && (
              <img
                src={habitat.backgroundImage}
                alt={habitat.title}
                className="preflight preflight-img size-full overflow-hidden rounded-t-2xl object-cover"
              />
            )}
            <div className="absolute inset-x-0 -bottom-7 flex items-center justify-center">
              <div className="relative">
                {habitat.profileImage ? (
                  <img
                    src={habitat.profileImage}
                    alt={habitat.title}
                    className="preflight preflight-img size-[108px] overflow-hidden rounded-full border border-white object-cover"
                  />
                ) : (
                  <div className="preflight flex size-[108px] items-center justify-center overflow-hidden rounded-full border border-white bg-gray-400 text-2xl font-bold text-white">
                    {habitat.title.charAt(0)}
                  </div>
                )}
                {habitat.zoo && (
                  <div className="absolute bottom-0 right-0 rounded-full bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                    <img
                      src={habitat.zoo.logo}
                      alt={habitat.zoo.name}
                      className="preflight preflight-img size-[44px] overflow-hidden rounded-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full px-4 pt-5">
            <h4 className="preflight preflight-h4 text-center text-2xl/none font-bold text-green-900">Habitat Supporter</h4>
            <p className="preflight preflight-p mt-3 text-center text-sm leading-5 text-stone-500">
              Get unlimited access to <span className="font-bold">{habitat?.title ?? 'this habitat'}</span>. Choose how much
              you want to contribute to support this habitat.
            </p>
          </div>

          <ProductIntervalSelector
            value={productInterval}
            onChange={(interval) => {
              if (interval === productInterval) return;
              setProduct(null);
              setProductInterval(interval);
            }}
          />

          <div
            className="my-2 grid w-full gap-2 px-2"
            style={{ gridTemplateColumns: `repeat(${products?.length ?? 0}, 1fr)` }}
          >
            {products?.map((p) => (
              <button
                role="option"
                type="button"
                aria-selected={p._id === product?._id}
                className={clsx(
                  'preflight preflight-button flex h-20 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-[#CFDEAC] px-0.5 py-3.5 text-secondary-green',
                  getProductColor(p.productLookupKey),
                )}
                key={p._id}
                onClick={() => setProduct(p)}
              >
                <h6 className="preflight preflight-h6 text-center text-base font-bold text-secondary-green">
                  {getProductTitle(p.productLookupKey)}
                </h6>
                <span className="text-lg/none">
                  <ProductPrice
                    currency={p.price.currency}
                    price={p.price.price}
                    className="font-normal leading-none [font-size:inherit]"
                  />
                  /{productInterval === 'month' ? 'Mo' : 'Yr'}
                </span>
              </button>
            ))}
          </div>

          <div className="w-full px-4">
            <Button
              disabled={isPending || !habitat || !product?.eligibility?.allowed}
              size="manual"
              className="w-full px-4 py-3 text-base/none font-bold"
              onClick={() => {
                if (!product || !habitat) return;
                checkout({
                  productId: product._id,
                  priceId: product.price.priceId,
                  associatedHabitatIds: [habitatId],
                  successPath: `/h/${habitat.zoo.slug}/${habitat.slug}?tab=sponsors&sponsored=1`,
                  cancelPath: `/h/${habitat.zoo.slug}/${habitat.slug}?ppt=sponsor`,
                });
                trackMP('request-checkout', {
                  productId: product._id,
                  priceId: product.price.priceId,
                  productLookupKey: product.productLookupKey,
                  habitatId,
                  entryPoint,
                });
              }}
            >
              {!product ? 'Select an Option' : 'Support Now'}
            </Button>
            {product && !product.eligibility?.tierChangeableUserProductId && (
              <ProductEligibilityResult result={product.eligibility} className="mt-3" />
            )}
          </div>

          <button
            type="button"
            className="preflight preflight-button flex items-center justify-center gap-1 text-sm leading-5 text-stone-500"
            onClick={() => setShowLearnMore(!showLearnMore)}
          >
            <span>Where does your support go?</span>
            <ArrowRight className="size-4" />
          </button>
        </div>

        {/* learn more */}
        <div
          aria-hidden={!showLearnMore}
          className="absolute inset-0 bg-white transition-transform aria-hidden:pointer-events-none aria-hidden:translate-x-full"
        >
          <div ref={learnMoreRef} className="flex flex-col items-center gap-3.5 px-4 pb-7 pt-5">
            <button
              type="button"
              className="preflight preflight-button flex items-center justify-center gap-1 text-sm leading-5 text-stone-500"
              onClick={() => setShowLearnMore(false)}
            >
              <ArrowRight className="size-4 rotate-180" />
              <span>Back</span>
            </button>
            <h4 className="preflight preflight-h4 max-w-[284px] text-center text-xl/none font-bold text-green-900">
              What do animals do when no one&apos;s around watching?
            </h4>
            <p className="preflight preflight-p max-w-[308px] text-center text-[13px]/5 text-stone-500">
              Now you can find out. Behind this camera is a world-class facility committed to transparency — real animal
              care, uncut, unscripted, and unfiltered.
            </p>
            <img
              src={LEARN_MORE_PLACEHOLDER_IMAGE}
              width={301}
              height={235}
              alt="Animal care behind the camera"
              className="preflight preflight-img overflow-hidden rounded-lg object-cover"
            />
            <p className="preflight preflight-p max-w-[308px] text-center text-[13px]/5 text-stone-500">
              Your subscription keeps that transparent window open for the world to see.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsorshipSubscription;
